import classNames from 'classnames';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';

import { Alert } from '@feathr/components';
import { useActiveListingsPolling } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';

import ImporterAlertMessage from './ImporterAlertMessage';
import type { IImportersAlertProps } from './ImportersAlert.types';

import * as styles from './ImportersAlert.css';

/**
 * Lists the project's imports that were pending when the step opened, and polls them until none
 * is pending.
 */
function PolledImportersAlert({
  eventId,
  partnersCollection,
}: Readonly<IImportersAlertProps>): JSX.Element | null {
  const { Importers } = useStore();
  const [importerIds, setImporterIds] = useState<string[]>([]);

  useEffect(() => {
    async function loadImporterIds(): Promise<void> {
      const pending = Importers.list(
        { filters: { event: eventId, state: 'pending' } },
        { reset: true },
      );
      await when(() => !pending.isPending);
      setImporterIds(pending.models.map((importer) => importer.id));
    }

    void loadImporterIds();
  }, []);

  const importers = importerIds.map((id) => Importers.get(id));
  useActiveListingsPolling(
    Importers,
    importers.some((importer) => Importers.isActiveForRefresh(importer)),
  );

  // A draft has no line, so an alert that lists only drafts would render an empty box.
  if (importers.every((importer) => importer.get('state') === 'draft')) {
    return null;
  }

  function handleRefresh(): void {
    partnersCollection.refreshApiCache();
  }

  return (
    <Alert
      className={classNames(styles.alert, styles.inlineLinks)}
      title={
        <>
          {importers.map((importer) => (
            <ImporterAlertMessage
              importer={importer}
              key={importer.id}
              onRefresh={handleRefresh}
              showReason={true}
            />
          ))}
        </>
      }
      type={importers.some((importer) => importer.get('state') === 'failed') ? 'warning' : 'info'}
    />
  );
}

export default observer(PolledImportersAlert);
